<template>
    <div class="livevideoWrapper" :class="fadeClass" :style="{ transition: fadeTime + 'ms linear opacity' }">
        <audio
            ref="liveAudioPlayer"
            :autoplay="autoplay"
            :muted="!display.includes('audio')"
        />
        <video
            ref="liveVideoPlayer"
            :autoplay="autoplay"
            playsinline
            class="live-video"
            :class="displayClass"
            :muted="!display.includes('audio')"
            @contextmenu.prevent.stop
            @playing="showPlayButton = false"
            @pause="showPlayButton = true"
        />

        <div class="is-live-indicator" v-if="isLiveIndicator">
            <span />{{ $t('ceremony.live_in_aula') }}
        </div>

        <div v-if="controls" class="player-controls">
            <button class="btn btn-primary" @click="playButton" v-if="showPlayButton">
                <icon name="play" />
            </button>
        </div>
    </div>
</template>

<script>
    import FadeMixin from '../mixins/FadeMixin';
    import { SrsRtcPlayerAsync } from '../../libraries/srs.sdk';
    import { mapState } from 'vuex';

    export default {

        mixins: [
            FadeMixin,
        ],

        props: {
            livestream: {
                type: String,
                default: null,
            },

            autoplay: {
                type: Boolean,
                default: false,
            },

            playing: {
                type: Boolean,
                default: true,
            },

            controls: {
                type: Boolean,
                default: true,
            },

            token: {
                type: String,
                required: true,
            },

            isLiveIndicator: {
                type: Boolean,
                default: false,
            },

            display: {
                type: Array,
                default() {
                    return [
                        'audio',
                        'video',
                    ];
                },
            },

            audioOutputDeviceId: {
                type: String,
                default: null,
            },
        },

        computed: {
            displayClass() {
                return this.display.map(displayType => `display-${displayType}`).join(' ');
            },

            ...mapState({
                companySettings: state => state.companySettings
            })
        },

        data() {
            return {
                player: null,
                durationTimer: null,
                emittedDuration: null,

                showPlayButton: true,
            };
        },

        watch: {
            livestream() {
                this.updatePlayer();
            },

            token() {
                this.updatePlayer();
            },

            playing() {
                if (this.playing) {
                    // When we want to play we have to reconnect
                    this.updatePlayer();
                } else {
                    // No playing means just pause the player
                    this.pause();
                }
            },

            audioOutputDeviceId() {
                if (!this.$refs.liveAudioPlayer || !this.$refs.liveAudioPlayer.setSinkId) {
                    return;
                }

                this.$refs.liveAudioPlayer.setSinkId(this.audioOutputDeviceId);
            },
        },

        mounted() {
            this.createPlayer();
        },

        methods: {
            isRendering() {
                return true;
            },

            isConnected() {
                return this.player?.pc?.connectionState === 'connected';
            },

            connected() {
                if (this.connectInterval) {
                    clearInterval(this.connectInterval);
                }

                return new Promise((resolve, reject) => {
                    if (!this.player) {
                        return reject();
                    }

                    if (this.isRendering() && this.isConnected()) {
                        return resolve();
                    }

                    this.connectInterval = setInterval(() => {
                        if (!this.player) {
                            return reject();
                        }

                        if (this.isRendering() && this.isConnected()) {
                            if (this.connectInterval) {
                                clearInterval(this.connectInterval);
                            }

                            resolve();
                        }
                    }, 100);
                });
            },

            playButton() {
                this.createPlayer();

                this.$refs.liveVideoPlayer.play();
                this.$refs.liveAudioPlayer.play();
            },

            play() {
                const playResponse = this.player.play(this.$refs.liveVideoPlayer, this.$refs.liveAudioPlayer, this.getSrc());
                // const playResponse = this.player.play();

                if (playResponse && (playResponse.catch || playResponse.then)) {
                    playResponse.catch((error) => {
                        this.connected().then(() => {
                            console.error(error);
                            this.fadeIn();
                        });
                    });

                    playResponse.then(() => {
                        this.$refs.liveVideoPlayer.play();

                        this.connected().then(() => {
                            this.fadeIn();
                        });
                    });
                } else {
                    this.connected().then(() => {
                        this.fadeIn();
                    });
                }
            },

            pause() {
                this.fadeOut().then(() => {
                    if (this.$refs.liveVideoPlayer) {
                        this.$refs.liveVideoPlayer.pause();
                    }
                });
            },

            createPlayer() {
                if (!this.companySettings) {
                    return;
                }

                this.player = this.createLivePlayer();

                // Spoof methods for the fade mixin
                this.player.remainingTime = () => {
                    return 99999999;
                };

                this.player.volume = (volume) => {
                    if (!this.$refs.liveVideoPlayer) {
                        return;
                    }

                    if (volume === undefined) {
                        return this.$refs.liveVideoPlayer.volume;
                    }

                    return this.$refs.liveVideoPlayer.volume = volume;
                };

                if (!this.player) {
                    return;
                }

                // this.player.on('loadeddata', () => {
                if (this.autoplay && this.playing) {
                    console.log('Livestream video autoload');

                    this.play();
                }

                this.emittedDuration = null;

                if (this.connectInterval) {
                    clearInterval(this.connectInterval);
                }

                if (this.durationTimer) {
                    clearInterval(this.durationTimer);
                }

                this.durationTimer = setInterval(() => {
                    if (this.isFinished()) {
                        return;
                    }

                    const currentDuration = this.getFullDuration();

                    if (this.emittedDuration === currentDuration && this.emittedDuration !== null) {
                        return;
                    }

                    this.emittedDuration = currentDuration;
                    this.$emit('durationUpdated', currentDuration);
                }, 1000);

                this.initFadeTimer();
            },

            getFullDuration() {
                return 0;
            },

            isFinished() {
                return false;
            },

            updatePlayer() {
                console.log('Update player');

                if (this.player) {
                    this.player.close();
                }

                this.createPlayer();
            },

            getSrc() {
                const src = this.companySettings?.streaming?.streaming + '/live/' + this.livestream;

                console.log('Starting livestream from ' + src);

                return src;
            },

            createLivePlayer() {
                return new SrsRtcPlayerAsync({
                    apiUrl: this.companySettings?.streaming?.api,
                    apiToken: this.token,

                    onConnectionStateChange: (connectionState) => {
                        console.log('Live player connection state changed: ' + connectionState.target.connectionState);
                        if (connectionState.target.connectionState === 'disconnected') {
                            this.pause();
                        }

                        if (connectionState.target.connectionState === 'failed') {
                            this.destroyPlayer();

                            setTimeout(() => {
                                this.createPlayer();
                            }, 5000);
                        }
                    },
                });
            },

            getCurrentVolume() {
                if (!this.player) {
                    return 'no-player';
                }

                return Math.round(this.player.volume() * 100) + '%';
            },

            destroyPlayer() {
                if (this.player) {
                    console.log('Livestream video destroy');
                    this.pause();

                    setTimeout(() => {
                        this.player.close();
                    }, this.fadeTime);
                }

                if (this.connectInterval) {
                    clearInterval(this.connectInterval);
                }

                if (this.durationTimer) {
                    clearInterval(this.durationTimer);
                }

                this.destroyFadeTimer();
            },
        },

        beforeDestroy() {
            this.destroyPlayer();
        },
    };
</script>

<style lang="scss">

    $mobile: 767px;

    .vjs-audio-only-skin {
        width: 100% !important;
        min-width: 200px !important;
        height: 30px !important;
        background: transparent !important;

        .vjs-big-play-button {
            display: none !important;
        }

        .vjs-control-bar {
            display: flex !important;
            border-radius: 30px !important;
            background-color: #e3e3e3 !important;
            color: #000 !important;
            opacity: 1 !important;
        }
    }

    .livevideoWrapper {
        width: 100%;
        height: 100%;
        opacity: 0;
        position: relative;

        &.in {
            opacity: 1;
        }

        &.out {
            opacity: 0;
        }

        &:before,
        .live-video {
            display: block;
            vertical-align: top;
            box-sizing: border-box;
            color: #fff;
            background-color: #000;
            position: relative;
            padding: 0;
            font-size: 10px;
            line-height: 1;
            font-weight: normal;
            font-style: normal;
            font-family: Arial, Helvetica, sans-serif;
            word-break: initial;
            width: 100%;
            height: 100%;
            object-fit: cover;
            opacity: 0;
        }

        .live-video.display-video {
            opacity: 1;
        }

        .player-controls {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            padding: 15px;

            button {
                pointer-events: all;
            }
        }

        .is-live-indicator {
            color: #ffffff;
            background-color: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            pointer-events: none;

            position: absolute;
            top: 10px;
            right: 10px;
            border-radius: 10px;
            padding: 2px 7px;

            span {
                width: 10px;
                height: 10px;
                background: #ed3131;
                border-radius: 50%;
                margin-right: 5px;
            }
        }

        &:before {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: block;
            content: '';
            opacity: 1;
        }
    }

</style>
